import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface SaveSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchCriteria: {
    searchQuery: string;
    selectedMake: string;
    selectedModel: string;
    yearRange: { min: number; max: number };
    priceRange: { min: number; max: number };
    selectedCondition: string;
    selectedTransmission: string;
    selectedDrivetrain: string;
    selectedFuelType: string;
    selectedCategory: string;
    searchLocation: string;
    searchRadius: number;
  };
  onSaveSuccess: () => void;
}

export default function SaveSearchModal({
  isOpen,
  onClose,
  searchCriteria,
  onSaveSuccess,
}: SaveSearchModalProps) {
  const [searchName, setSearchName] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!searchName.trim()) {
      setError('Please enter a name for this search');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('You must be logged in to save searches');
        setIsSaving(false);
        return;
      }

      const { error: saveError } = await supabase
        .from('saved_searches')
        .insert({
          user_id: user.id,
          name: searchName.trim(),
          search_query: searchCriteria.searchQuery || null,
          make: searchCriteria.selectedMake !== 'All Makes' ? searchCriteria.selectedMake : null,
          model: searchCriteria.selectedModel !== 'All Models' ? searchCriteria.selectedModel : null,
          year_min: searchCriteria.yearRange.min,
          year_max: searchCriteria.yearRange.max,
          price_min: searchCriteria.priceRange.min,
          price_max: searchCriteria.priceRange.max,
          condition: searchCriteria.selectedCondition !== 'All Conditions' ? searchCriteria.selectedCondition : null,
          transmission: searchCriteria.selectedTransmission !== 'All Transmissions' ? searchCriteria.selectedTransmission : null,
          drivetrain: searchCriteria.selectedDrivetrain !== 'All Drivetrains' ? searchCriteria.selectedDrivetrain : null,
          fuel_type: searchCriteria.selectedFuelType !== 'All Fuel Types' ? searchCriteria.selectedFuelType : null,
          category: searchCriteria.selectedCategory !== 'All Categories' ? searchCriteria.selectedCategory : null,
          location: searchCriteria.searchLocation || null,
          radius: searchCriteria.searchRadius,
          notifications_enabled: notificationsEnabled,
        });

      if (saveError) throw saveError;

      onSaveSuccess();
      onClose();
      setSearchName('');
    } catch (err) {
      console.error('Error saving search:', err);
      setError('Failed to save search. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="mb-6">
          <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
            <i className="ri-bookmark-line text-2xl text-teal-600"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Save This Search</h2>
          <p className="text-gray-600 mt-2">
            Get notified when new vehicles matching your criteria appear in your area
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Search Name
            </label>
            <input
              type="text"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              placeholder="e.g., Toyota SUVs under $30k"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
              maxLength={100}
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Search Criteria</h3>
            <div className="space-y-2 text-sm text-gray-600">
              {searchCriteria.selectedMake !== 'All Makes' && (
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500"></i>
                  <span>Make: {searchCriteria.selectedMake}</span>
                </div>
              )}
              {searchCriteria.selectedModel !== 'All Models' && (
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500"></i>
                  <span>Model: {searchCriteria.selectedModel}</span>
                </div>
              )}
              {searchCriteria.selectedCategory !== 'All Categories' && (
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500"></i>
                  <span>Category: {searchCriteria.selectedCategory}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-teal-500"></i>
                <span>Year: {searchCriteria.yearRange.min} - {searchCriteria.yearRange.max}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-teal-500"></i>
                <span>
                  Price: ${searchCriteria.priceRange.min.toLocaleString()} - ${searchCriteria.priceRange.max.toLocaleString()}
                </span>
              </div>
              {searchCriteria.searchLocation && (
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500"></i>
                  <span>
                    Location: {searchCriteria.searchLocation} ({searchCriteria.searchRadius === 1000 ? 'Any distance' : `${searchCriteria.searchRadius} miles`})
                  </span>
                </div>
              )}
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={notificationsEnabled}
              onChange={(e) => setNotificationsEnabled(e.target.checked)}
              className="w-5 h-5 mt-0.5 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
            />
            <div>
              <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
                Enable notifications
              </span>
              <p className="text-xs text-gray-500 mt-1">
                Receive alerts when new vehicles match your search
              </p>
            </div>
          </label>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 px-4 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="ri-save-line mr-2"></i>
                Save Search
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
